import { useMemo } from "react";

import type { CptFrequencyExportData } from "../../services/reports.service";
import { groupCptAccountPhysicianRows } from "../../utils/groupCptAccountPhysicianRows";
import {
  billingAccountTypeLabel,
  billingDateBasisLabel,
} from "./billingReportLabels";
import styles from "./BillingReportPrint.module.css";

interface CptFrequencyPrintReportProps {
  report: CptFrequencyExportData;
}

export function CptFrequencyPrintReport({
  report,
}: CptFrequencyPrintReportProps) {
  const groupedRows = useMemo(
    () => groupCptAccountPhysicianRows(report.rows),
    [report.rows]
  );

  return (
    <main className={styles.page} data-billing-report-document="true">
      <h1 className={styles.title}>GCP CPT COUNT BY CLIENT</h1>
      <section className={styles.metadata}>
        <p className={styles.metadataLine}>
          Generated At: {report.generatedAt}
        </p>
        <p className={styles.metadataLine}>
          Filtered By: {billingDateBasisLabel(report.dateBasis)}
        </p>
        <p className={styles.metadataLine}>
          From: {report.startDate} - {report.endDate}
        </p>
        <p className={styles.metadataLine}>
          Account Type:{" "}
          {report.accountTypes.map(billingAccountTypeLabel).join(", ")}
        </p>
        {report.activeFilters.map((filter) => (
          <p className={styles.metadataLine} key={filter.label}>
            {filter.label}: {filter.value}
          </p>
        ))}
      </section>

      {report.unassignedCaseCount > 0 && (
        <p className={styles.warning}>
          RECONCILIATION REQUIRED: {report.unassignedCaseCount} qualifying
          case(s) have an unassigned billing account.
        </p>
      )}
      {report.backfilledTcCaseCount > 0 && (
        <p className={styles.warning}>
          TC SNAPSHOT PROVENANCE: {report.backfilledTcCaseCount} TC case
          snapshot(s) were initialized from current billing facts during
          reporting rollout.
        </p>
      )}
      {report.reconciliationCases.length > 0 && (
        <table className={`${styles.table} ${styles.reviewTable}`}>
          <thead>
            <tr>
              <th>Review Case</th>
              <th>Issue / Provenance</th>
            </tr>
          </thead>
          <tbody>
            {report.reconciliationCases.flatMap((reportCase) =>
              reportCase.issues.map((issue) => (
                <tr key={`${reportCase.caseId}:${issue}`}>
                  <td>{reportCase.caseNumber}</td>
                  <td>{issue}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Account Type</th>
            <th>Account Name</th>
            <th>Physician Name</th>
            <th>Prefix</th>
            <th>CPT / Report Code</th>
            <th className={styles.number}>Count Total</th>
          </tr>
        </thead>
        <tbody>
          {groupedRows.map(
            ({ row, accountRowSpan, physicianRowSpan, prefixRowSpan }) => (
              <tr
                key={`${row.accountType}:${row.accountId}:${row.physicianId}:${row.prefixId}:${row.cptCode}:${row.stainClassification}`}
              >
                {accountRowSpan > 0 && (
                  <>
                    <td rowSpan={accountRowSpan}>
                      {billingAccountTypeLabel(row.accountType)}
                    </td>
                    <td rowSpan={accountRowSpan}>{row.accountName}</td>
                  </>
                )}
                {physicianRowSpan > 0 && (
                  <td rowSpan={physicianRowSpan}>{row.physicianName}</td>
                )}
                {prefixRowSpan > 0 && (
                  <td rowSpan={prefixRowSpan}>{row.prefixCode}</td>
                )}
                <td>{row.cptCode}</td>
                <td className={styles.number}>{row.count}</td>
              </tr>
            )
          )}
        </tbody>
      </table>

      <table className={`${styles.table} ${styles.summary}`}>
        <thead>
          <tr>
            <th>CPT / Report Code</th>
            <th className={styles.number}>Total Count</th>
          </tr>
        </thead>
        <tbody>
          {report.summary.map((row) => (
            <tr key={`${row.cptCode}:${row.stainClassification}`}>
              <td>{row.cptCode}</td>
              <td className={styles.number}>{row.count}</td>
            </tr>
          ))}
          <tr className={styles.total}>
            <td>Grand Total</td>
            <td className={styles.number}>{report.grandTotal}</td>
          </tr>
        </tbody>
      </table>
    </main>
  );
}
